import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { Flag } from '@/components/QueueFlags/Flag';
import { FlagInfo } from '@/components/QueueFlags/FlagInfo';
import { QueueFlagsContext } from '@/components/QueueFlags/QueueFlagsContext';
import { getPreviewUrl, getOriginalUrl } from '@/helpers/documentHelpers';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetDocumentExtractQuery } from '@/services/genai';
import {
  Box,
  Button,
  Card,
  Icon,
  Modal,
  SelectInput,
  Spinner,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  ActivationDocumentTypes,
  ActivationPackagePhotoCategory,
  AllDocumentTypes,
  DocumentSortField,
  DocumentStatus,
  documentTitles,
  DocumentTypes,
  documentTypeToRequirementFlagSettingsMap,
  GeothermalDesignDocumentTypes,
  GeothermalDocumentTypes,
  HVACDocumentTypes,
  HVACInstallPackageFileCategory,
  InstallationPackagePhotoCategory,
  InstallDocumentTypes,
  InterconnectionAgreementDocumentType,
  supportedDocumentExtractTypes,
  UserPermissions,
  VerifyExistingSystemDocumentTypes,
} from 'types';

import { useUploadDocumentMutation, useWatchDocumentVariants } from '../../../services/documents';
import RequirePermissions from '../../auth/requirePermissions';
import { DocumentPreview, DocumentPreviewModal } from '../../documents/DocumentPreviewModal';

import CheckboxHeader from './CheckboxHeader';
import { DocumentListItem } from './DocumentListItem';

type DocumentListProps = {
  documentTypeFilter: Array<AllDocumentTypes> | undefined;
  additionalDocumentsToFetch?: Array<{
    types: Array<AllDocumentTypes> | undefined;
    status?: Array<DocumentStatus>;
  }>;
  showDocumentActions?: boolean;
  allowArchive?: boolean;
  showTypeLabelOnItems?: boolean;
  showUploadButton?: boolean;
  className?: string;
  title?: string;
  baseCategory?: string;
  uploadContext?: string;
  grouped?: boolean;
  showCheckboxes?: boolean;
  checkboxCategories?: AllDocumentTypes[];
  isPackageEditable?: boolean;
};

const TitleWithCount = ({ title, documentsLength }: { title: string; documentsLength: number }) => (
  <Box as="h3" fontWeight="medium" fontSize="md" direction="row" gap="md" alignItems="center">
    <Box>{title ? title : 'Documents'}</Box>
    {documentsLength > 1 && <Box fontSize="md" fontWeight="light">{`${documentsLength} files`}</Box>}
  </Box>
);

export function DocumentList({
  documentTypeFilter = [],
  additionalDocumentsToFetch,
  showDocumentActions = true,
  allowArchive = true,
  showTypeLabelOnItems = true,
  showUploadButton = true,
  className,
  title = '',
  baseCategory,
  uploadContext,
  grouped = true,
  showCheckboxes = false,
  checkboxCategories = [],
  isPackageEditable = true,
}: DocumentListProps) {
  const { id } = useParams<{ id: any }>();
  const { flagDrivenPackageReview } = useFlags();
  const queryParams = new URLSearchParams(useLocation().search);
  const scrollTo = queryParams.get('scrollTo');

  const { data: primaryDocuments = [], isLoading: isPrimaryLoading } = useGetDocumentsQuery({
    accountId: id,
    documentTypeFilter,
    sort: DocumentSortField.ID_DESC,
  });

  const additionalQueries = useMemo(() => {
    return (
      additionalDocumentsToFetch?.map((additionalDoc) => ({
        accountId: id,
        documentTypeFilter: additionalDoc.types,
        documentStatusFilter: additionalDoc.status,
        sort: DocumentSortField.ID_DESC,
      })) || []
    );
  }, [id, additionalDocumentsToFetch]);

  const additionalResults = additionalQueries.map((queryArgs) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useGetDocumentsQuery(queryArgs),
  );

  const additionalDocuments = useMemo(() => {
    return additionalResults.flatMap((result) => result.data || []);
  }, [additionalResults]);

  const isAdditionalLoading = additionalResults.some((result) => result.isLoading);

  const documents = useMemo(() => {
    const docMap = new Map();
    [...primaryDocuments, ...additionalDocuments].forEach((doc) => {
      if (doc.id) {
        docMap.set(doc.id.toString(), doc);
      }
    });
    return Array.from(docMap.values());
  }, [primaryDocuments, additionalDocuments]);

  const isDocumentsLoading = isPrimaryLoading || isAdditionalLoading;
  const { isPhone } = useBreakpoint();
  const { getFlag, getFlagsByFlagType, shouldHide, getVisibleFlags } = useContext(QueueFlagsContext);
  const uniqueDocumentType = documentTypeFilter?.length === 1 ? documentTypeFilter[0] : undefined;
  const flagType = uniqueDocumentType && documentTypeToRequirementFlagSettingsMap[uniqueDocumentType];
  const currentFlag = getFlag(title, undefined, false);
  const currentFlags = uniqueDocumentType && flagType ? getFlagsByFlagType(flagType, false, title) : [];
  const allFlags = [...(currentFlags ? currentFlags : []), ...(currentFlag ? [currentFlag] : [])];
  const visibleFlags = getVisibleFlags(allFlags);
  const hasFlags = Boolean(flagDrivenPackageReview ? visibleFlags?.length : currentFlag);
  const { isOpen: isUploadModal, handleClose: handleUploadModalClose, handleOpen: openUploadModal } = useOpenClose();
  const [uploadDocument] = useUploadDocumentMutation();
  // Track the most recent v2 upload job so its thumbnail/preview variants pop
  // in automatically when the worker finishes (no manual refresh). The LIST
  // invalidation refreshes all docs regardless of which upload it came from.
  const [variantJobId, setVariantJobId] = useState<string | undefined>(undefined);
  useWatchDocumentVariants({ accountId: id, jobId: variantJobId ?? '' }, { skip: !variantJobId });
  const isLoading = isDocumentsLoading;
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const filteredCheckboxCategories = useMemo(() => {
    if (documentTypeFilter.length === 1) {
      return checkboxCategories.filter((category) => !documentTypeFilter?.includes(category));
    }
    // allow all categories if more than one document type is being shown in the document list
    return checkboxCategories;
  }, [documentTypeFilter, checkboxCategories]);

  const sectionRefs = useMemo(
    () => ({
      documents: createRef(),
    }),
    [],
  ) as any;

  useEffect(() => {
    if (scrollTo) {
      sectionRefs[scrollTo]?.current?.scrollIntoView();
    }
  }, [scrollTo, sectionRefs]);

  // These values must match the DocumentTypes enum in the API.
  const documentTypeOptions = useMemo(() => {
    const allDocuments = {
      ...DocumentTypes,
      ...InstallDocumentTypes,
      ...ActivationDocumentTypes,
      ...ActivationPackagePhotoCategory,
      ...InterconnectionAgreementDocumentType,
      ...InstallationPackagePhotoCategory,
      ...VerifyExistingSystemDocumentTypes,
      ...HVACInstallPackageFileCategory,
      ...HVACDocumentTypes,
      ...GeothermalDocumentTypes,
      ...GeothermalDesignDocumentTypes,
    };
    const documentTypes = Object.keys(allDocuments).map((key) => {
      return { value: key, label: documentTitles[key as AllDocumentTypes] };
    });
    if (documentTypeFilter.length > 0) {
      return documentTypes.filter((dt) => documentTypeFilter.includes(dt.value as AllDocumentTypes));
    }
    return documentTypes;
  }, [documentTypeFilter]);

  const [files, setFiles] = useState<FileList | undefined>();

  const [manualTypeSelection, setManualTypeSelection] = useState<{ value: string; label: string } | undefined>();

  const documentTypeOptionSelection =
    documentTypeOptions.length === 1 ? documentTypeOptions[0] : manualTypeSelection;

  const onFileChange = useCallback(
    (event: any) => {
      setFiles(event.target?.files);
    },
    [setFiles],
  );

  const closeUploadModal = useCallback(() => {
    setFiles(undefined);
    handleUploadModalClose();
  }, [setFiles, handleUploadModalClose]);

  const onTypeChange = useCallback(
    (event: any) => setManualTypeSelection(event.target.value),
    [setManualTypeSelection],
  );

  const handleUpload = useCallback(
    async (event?: any) => {
      const filesToUpload: FileList | undefined = event?.target?.files ?? files;
      if (!filesToUpload || !filesToUpload.length) return;

      const data = new FormData();

      if (grouped) {
        data.append('grouped', 'true');
      }
      if (uploadContext) {
        data.append('context', uploadContext);
      }
      if (documentTypeOptionSelection && 'value' in documentTypeOptionSelection) {
        data.append('type', documentTypeOptionSelection.value);
      }
      for (let i = 0; i < filesToUpload.length; i++) {
        data.append(`files[${i}]`, filesToUpload[i]);
      }

      closeUploadModal();

      try {
        const { jobId } = await uploadDocumentsWithToast({
          uploadDocument,
          accountId: id,
          formData: data,
          invalidateAllTags: false,
          files: filesToUpload,
        });
        if (jobId) setVariantJobId(jobId);
      } catch {
        // Upload failed — the helper already surfaced an error toast.
      }
    },
    [files, grouped, uploadContext, documentTypeOptionSelection, id, uploadDocument, closeUploadModal],
  );

  const renderFlaggedContent = () => {
    if (!hasFlags) return null;

    if (flagDrivenPackageReview) {
      return (
        <Box width="100">
          <FlagInfo dataKey={title} flagType={flagType} hasFlags={hasFlags} />
        </Box>
      );
    }

    return (
      <Box direction="row" alignItems="center">
        <Box display="block" childGap="xs" padding={{ base: 'md lg', tablet: 'sm md md 0' }}>
          <Icon name="flag" size="lg" color="danger" />
        </Box>
        <Box
          display="block"
          padding={{ base: '0 lg', tablet: 'md md md 0' }}
          margin="0 2xl 0 0"
          width={{ base: '2xl' }}
        >
          <FlagInfo dataKey={title} flagType={flagType} hasFlags={hasFlags} />
        </Box>
      </Box>
    );
  };

  const renderContentSection = () => {
    const flagButton = !shouldHide && title && (
      <Flag dataKey={title} flagType={flagType} hasFlags={hasFlags} type={uniqueDocumentType} />
    );

    if (flagDrivenPackageReview && flagType) {
      return (
        <Box
          childGap="lg"
          padding="lg"
          direction="column"
          justifyContent="space-between"
          borderColor={hasFlags ? 'danger-500' : 'separator'}
          borderWidth={hasFlags ? '0 0 0 md' : '0'}
          ref={sectionRefs.documents}
          className={`document-list-content ${hasFlags ? 'flagged' : ''} ${checkedItems.length ? 'checked' : ''}`}
        >
          {hasFlags && renderFlaggedContent()}
          <Box direction="row" justifyContent="space-between" gap="sm">
            <TitleWithCount title={title} documentsLength={documents?.length} />
            <Box direction="row" gap="md" alignItems="center">
              {flagButton}
              {showUploadButton && (
                <RequirePermissions
                  permissions={[UserPermissions.admin, UserPermissions.editor]}
                  checkAllPermissions={false}
                >
                  {documentTypeFilter?.length === 1 ? (
                    <ResettingFileUpload
                      labelText="Upload"
                      id={`upload-${documentTypeFilter[0]}-photos`}
                      name={`${documentTypeFilter[0]}-files`}
                      size="sm"
                      onChange={handleUpload}
                      multiple
                      buttonText={!isPhone ? 'Upload' : null}
                      onClick={(event: any) => event.stopPropagation()}
                      isDisabled={!isPackageEditable}
                    />
                  ) : (
                    <Button
                      size="sm"
                      variant="primary"
                      tone="neutral"
                      iconPrefix="upload"
                      onClick={openUploadModal}
                      isDisabled={!isPackageEditable}
                    >
                      {!isPhone && 'Upload'}
                    </Button>
                  )}
                </RequirePermissions>
              )}
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        childGap="lg"
        padding="lg"
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        ref={sectionRefs.documents}
      >
        {hasFlags && renderFlaggedContent()}
        <Box gap="sm">
          <TitleWithCount title={title} documentsLength={documents?.length} />
        </Box>
        <Box direction="row" gap="md" alignItems="center">
          {showUploadButton && (
            <RequirePermissions
              permissions={[UserPermissions.admin, UserPermissions.editor]}
              checkAllPermissions={false}
            >
              <Button
                size="sm"
                variant="primary"
                tone="neutral"
                iconPrefix="upload"
                onClick={openUploadModal}
                isDisabled={!isPackageEditable}
              >
                {!isPhone && 'Upload'}
              </Button>
            </RequirePermissions>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Modal isOpen={isUploadModal} onDismiss={closeUploadModal} maxWidth="4xl" ariaLabelledBy="uploadDocHeader">
        <Modal.Header id="uploadDocHeader" title="Upload Documents" onDismiss={closeUploadModal} />
        <Modal.Body background="secondary" childGap="lg">
          <SelectInput
            size="md"
            id="required"
            label="Document Type"
            value={documentTypeOptionSelection}
            onChange={onTypeChange}
            options={documentTypeOptions}
            isRequired
            isDisabled={documentTypeOptions.length === 1}
            menuPortalTarget={document.body}
          />
          <ResettingFileUpload
            buttonText="Choose File(s)"
            name="files"
            labelText="Choose File(s)"
            id="files"
            variant="secondary"
            tone="neutral"
            multiple
            required
            requiredIndicator=""
            onChange={onFileChange}
            files={files}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" isDisabled={isLoading} onClick={closeUploadModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpload} isDisabled={!files || !files.length}>
            Upload Document
          </Button>
        </Modal.Footer>
      </Modal>
      <Card className={className} background={hasFlags ? 'danger' : 'primary'} style={{ overflow: 'visible' }}>
        <Box>
          <Box
            className={`document-list-header ${hasFlags ? 'flagged' : ''} ${checkedItems.length ? 'checked' : ''}`}
          >
            <Box>{renderContentSection()}</Box>
            {checkedItems.length ? (
              <CheckboxHeader
                documents={documents}
                allowArchive={allowArchive}
                checkedItems={checkedItems}
                setCheckedItems={setCheckedItems}
                checkboxCategories={filteredCheckboxCategories}
              />
            ) : null}
          </Box>
          <List
            isLoading={isLoading}
            items={documents as any}
            showDocumentActions={showDocumentActions && isPackageEditable}
            showTypeLabelOnItems={showTypeLabelOnItems}
            allowArchive={allowArchive}
            baseCategory={baseCategory}
            title={title}
            showCheckboxes={showCheckboxes}
            setCheckedItems={setCheckedItems}
            checkedItems={checkedItems}
            isPackageEditable={isPackageEditable}
          />
        </Box>
      </Card>
    </>
  );
}

interface ListProps {
  isLoading: boolean;
  items?: any[];
  showDocumentActions?: boolean;
  showTypeLabelOnItems?: boolean;
  allowArchive?: boolean;
  baseCategory?: string;
  title?: string;
  showCheckboxes?: boolean;
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>;
  checkedItems?: string[];
  isPackageEditable?: boolean;
}

function List({
  isLoading,
  items = [],
  showDocumentActions = true,
  showTypeLabelOnItems,
  allowArchive = true,
  baseCategory,
  title,
  showCheckboxes = false,
  setCheckedItems,
  checkedItems = [],
  isPackageEditable = true,
}: ListProps) {
  const totalCount = items?.length || 0;
  const [isFilePreviewModalOpen, setIsFilePreviewModalOpen] = useState(false);
  const { getFlag } = useContext(QueueFlagsContext);
  const { enableCustomPdfViewer = false } = useFlags();

  const documents: DocumentPreview[] = useMemo(
    () =>
      items?.flatMap((item) =>
        item.files.map((file: any, index: number) => {
          const indexedId = `${item.id}-${index}`;
          const flaggedItem = getFlag([item.id], file.originalName);
          const flaggedIndexedItem = getFlag([indexedId], file.originalName);
          const id = flaggedItem ? item.id : flaggedIndexedItem ? indexedId : `${item.id}-${index}`;
          const viewUrls = file?.viewUrls;

          return {
            id: id,
            documentId: item.id,
            url: getPreviewUrl(viewUrls) ?? '',
            originalUrl: getOriginalUrl(viewUrls),
            contentType: file?.contentType,
            fileName: file?.originalName,
            createdAt: item.meta.createdAt,
            type: item.type,
            pageNumber: index + 1,
          };
        }),
      ),
    [items, getFlag],
  );

  const [targetDoc, setTargetDoc] = useState<DocumentPreview>(documents[0]);
  const targetDocFlagSettingType = documentTypeToRequirementFlagSettingsMap[targetDoc?.type as AllDocumentTypes];

  const isTargetDocPdfOrImage =
    targetDoc?.contentType === 'application/pdf' || Boolean(targetDoc?.contentType?.startsWith('image/'));
  const isTargetDocExtractSupported = supportedDocumentExtractTypes.includes(targetDoc?.type as AllDocumentTypes);
  const { currentData: targetDocExtract } = useGetDocumentExtractQuery(targetDoc?.documentId ?? '', {
    skip:
      !isFilePreviewModalOpen ||
      !targetDoc?.documentId ||
      !enableCustomPdfViewer ||
      !isTargetDocPdfOrImage ||
      !isTargetDocExtractSupported,
  });
  const documentLayout = targetDocExtract?.response?.documentLayout;

  const togglePreviewModal = useCallback(
    (itemId: string, fileIndex: number) => {
      const documentId = `${itemId}-${fileIndex}`;
      const targetItem = items.find((item) => item.id === itemId);
      if (!targetItem) {
        return;
      }

      const targetFile = targetItem.files[fileIndex];
      if (!targetFile) {
        return;
      }

      const flaggedItem = getFlag([itemId], targetFile.originalName);
      const flaggedIndexedItem = getFlag([documentId], targetFile.originalName);
      const currentItemId = flaggedItem ? itemId : flaggedIndexedItem ? documentId : documentId;

      const targetPreview = documents.find((doc) => doc.id === currentItemId);
      if (targetPreview) {
        setTargetDoc(targetPreview);
        setIsFilePreviewModalOpen(true);
      }
    },
    [documents, items, getFlag],
  );

  return (
    <>
      <Card.Section padding="0">
        {totalCount > 0 ? (
          <>
            <DocumentPreviewModal
              targetDoc={targetDoc}
              flagType={targetDocFlagSettingType}
              setTargetDoc={setTargetDoc}
              isFilePreviewModalOpen={isFilePreviewModalOpen}
              documentList={documents}
              closeFilePreviewModal={() => setIsFilePreviewModalOpen(false)}
              title={title}
              baseCategory={baseCategory}
              enableCustomPdfViewer={enableCustomPdfViewer}
              documentLayout={documentLayout}
            />
            {items.map((item) => (
              <DocumentListItem
                item={item}
                key={item.id}
                showDocumentActions={showDocumentActions}
                showTypeLabel={showTypeLabelOnItems}
                allowArchive={allowArchive}
                baseCategory={baseCategory}
                onClick={(fileIndex: number) => {
                  if (typeof fileIndex !== 'number') {
                    return;
                  }
                  togglePreviewModal(item.id, fileIndex);
                }}
                flagType={documentTypeToRequirementFlagSettingsMap[item.type as AllDocumentTypes]}
                showCheckboxes={showCheckboxes}
                setCheckedItems={setCheckedItems}
                checkedItems={checkedItems}
                isPackageEditable={isPackageEditable}
              />
            ))}
          </>
        ) : (
          <Box
            display="block"
            textAlign="center"
            padding="lg"
            childGap="xl"
            borderColor="separator"
            borderWidth="xs 0 0 0"
          >
            {isLoading ? (
              <Spinner size="lg" />
            ) : (
              <>
                <Box fontSize="sm" color="body-secondary">
                  No documents exist for this account
                </Box>
              </>
            )}
          </Box>
        )}
      </Card.Section>
    </>
  );
}
